"use client";

import { c as _c } from "react/compiler-runtime";
import BullseyeIcon from "@/assets/icons/bullseye.svg";
import StatusNoDataSvg from "@/assets/icons/status-no-data.svg";
import StatusReachedSvg from "@/assets/icons/status-reached.svg";
import { useIsTouch } from "@/hooks/use-touch";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { CardMode, StatusType } from "../../target-card-state";
import styles from "./target-card-parts.module.scss";
export const TARGET_AIRTIME_COLOR = "#FF9F0F";
export const TARGET_SOV_COLOR = "rgba(200,200,200,0.85)";
export function TargetIndicator(t0) {
  const $ = _c(14);
  const {
    mode,
    value,
    metricColor,
    className
  } = t0;
  const label = mode === CardMode.Pb ? "PB" : "Target";
  let t1;
  if ($[0] !== className) {
    t1 = cn(styles.targetIndicator, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== metricColor) {
    t2 = {
      "--metric-color": metricColor
    };
    $[2] = metricColor;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = t2 as CSSProperties;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className={styles.metricDot} aria-hidden={true} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== label) {
    t5 = <span>{label}</span>;
    $[5] = label;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== value) {
    t6 = <span className={styles.indicatorValue}>{value}</span>;
    $[7] = value;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t1 || $[10] !== t3 || $[11] !== t5 || $[12] !== t6) {
    t7 = <div className={t1} style={t3}>{t4}{t5}{t6}</div>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}
const STATUS_VISUALS: Record<StatusType, {
  label: string;
  icon: React.ElementType;
  iconClass: string;
  labelClass: string;
} | null> = {
  [StatusType.Reached]: {
    label: "Reached",
    icon: StatusReachedSvg,
    iconClass: styles.statusIconSuccess,
    labelClass: styles.statusLabelSuccess
  },
  [StatusType.NewPb]: {
    label: "New PB",
    icon: StatusReachedSvg,
    iconClass: styles.statusIconSuccess,
    labelClass: styles.statusLabelSuccess
  },
  [StatusType.NoData]: {
    label: "No data",
    icon: StatusNoDataSvg,
    iconClass: styles.statusIconMuted,
    labelClass: styles.statusLabelMuted
  },
  [StatusType.NoDataAvailable]: {
    label: "No Data Available",
    icon: StatusNoDataSvg,
    iconClass: styles.statusIconMuted,
    labelClass: styles.statusLabelMuted
  },
  [StatusType.None]: null
};
export function StatusFaceIcon(t0) {
  const $ = _c(6);
  const {
    type,
    className
  } = t0;
  const visual = STATUS_VISUALS[type];
  if (!visual) {
    return null;
  }
  const Icon = visual.icon;
  let t1;
  if ($[0] !== className || $[1] !== visual.iconClass) {
    t1 = cn(visual.iconClass, className);
    $[0] = className;
    $[1] = visual.iconClass;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== Icon || $[4] !== t1) {
    t2 = <Icon width={14} height={14} aria-hidden={true} className={t1} />;
    $[3] = Icon;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export function StatusBadge(t0) {
  const $ = _c(11);
  const {
    type,
    className
  } = t0;
  const visual = STATUS_VISUALS[type];
  if (!visual) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn(styles.statusBadge, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== type) {
    t2 = <StatusFaceIcon type={type} />;
    $[2] = type;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== visual.label || $[5] !== visual.labelClass) {
    t3 = <span className={visual.labelClass}>{visual.label}</span>;
    $[4] = visual.label;
    $[5] = visual.labelClass;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

// Back-compat alias — kept so existing call sites can use semantic naming.
export const EmptyStateBadge = t0 => {
  const $ = _c(2);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = <StatusBadge type={StatusType.NoData} className={className} />;
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export function ProgressBar(t0) {
  const $ = _c(28);
  const {
    percent,
    isNoData,
    className,
    currentAirtime,
    targetValue,
    isTargetReached
  } = t0;
  const isTouch = useIsTouch();
  const [onClick, setOnClick] = useState(false);
  const rootRef = useRef(null);
  const showCurrentCapsule = !isNoData && !!currentAirtime && !isTargetReached;
  const canClick = isTouch && !isNoData && (showCurrentCapsule || targetValue != null);
  let t1;
  let t2;
  if ($[0] !== canClick || $[1] !== onClick) {
    t1 = () => {
      if (!(canClick && onClick)) {
        return;
      }
      const onPointerDown = event => {
        if (rootRef.current?.contains(event.target as Node)) {
          return;
        }
        setOnClick(false);
      };
      window.addEventListener("pointerdown", onPointerDown);
      return () => window.removeEventListener("pointerdown", onPointerDown);
    };
    t2 = [canClick, onClick];
    $[0] = canClick;
    $[1] = onClick;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const t3 = canClick && onClick && styles.progressBarOpen;
  const t4 = canClick && styles.progressBarTap;
  let t5;
  if ($[4] !== className || $[5] !== t3 || $[6] !== t4) {
    t5 = cn(styles.progressBar, t3, t4, className);
    $[4] = className;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== canClick) {
    t6 = canClick ? () => setOnClick(_temp) : undefined;
    $[8] = canClick;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className={styles.progressBarTrack} aria-hidden={true} />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== isNoData || $[12] !== percent) {
    t8 = <div className={styles.progressBarInner}>{isNoData ? <div className={styles.progressBarNoData} aria-hidden={true} /> : <div className={styles.progressBarFill} style={{
        "--progress-percent": `${percent}%`
      } as CSSProperties} />}</div>;
    $[11] = isNoData;
    $[12] = percent;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={styles.progressBarInset} aria-hidden={true} />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== currentAirtime || $[16] !== showCurrentCapsule) {
    t10 = showCurrentCapsule && <div className={styles.progressHoverValue}>{currentAirtime}</div>;
    $[15] = currentAirtime;
    $[16] = showCurrentCapsule;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== isNoData || $[19] !== isTargetReached || $[20] !== targetValue) {
    t11 = !isNoData && targetValue && <div className={cn(styles.progressTargetValue, isTargetReached && styles.progressTargetValueReached)}><BullseyeIcon width={14} height={14} aria-hidden={true} className={styles.progressTargetIcon} />{targetValue}</div>;
    $[18] = isNoData;
    $[19] = isTargetReached;
    $[20] = targetValue;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t5 || $[25] !== t6 || $[26] !== t8) {
    t12 = <div ref={rootRef} className={t5} data-name="Progress Bar" onClick={t6}>{t7}{t8}{t9}{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t5;
    $[25] = t6;
    $[26] = t8;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}
function _temp(value) {
  return !value;
}